import React, { useState } from 'react';
import { TaskResult, HealthCareTranscribeResponse, UserRequest } from '../types';
import { ChevronDown, ChevronUp, Copy, ThumbsUp, ThumbsDown, AlertTriangle } from 'lucide-react';
import { MedicalComprehendComponent } from './MedicalComprehendComponent';
import { WomensHealthGeneratedNotesComponent } from './WomensHealthGeneratedNotes';
import { WomensHealthHistoryAndPhysicalNotesComponent } from './WomensHealthHistoryAndPhysicalNotes';
import { KineticNotesGeneratedNotesComponent } from './KineticNotesGeneratedNotes';
import { AnnualWellWomanVisitNotesComponent } from './AnnualWellWomanVisitNotes';
import { PrenatalVisitNotesComponent } from './PrenatalVisitNotes';
import { PrenatalVisitInitialNotesComponent } from './PrenatalVisitInitialNotes';
import { isBestiHealthAccount, isKineticNotesAccount } from '../utils/brandUtils';

interface GeneratedNotesProps {
  finalResult: TaskResult | null;
  userRequest?: UserRequest | null;
}

export const GeneratedNotesComponent: React.FC<GeneratedNotesProps> = ({
  finalResult,
  userRequest
}) => {
  // Check if this is a Besti Health or Kinetic Notes account and has LLM analysis enhancement
  const isBestiHealth = isBestiHealthAccount();
  const isKineticNotes = isKineticNotesAccount();
  const llmEnhancement = userRequest?.llmAnalysisEnhancement;
  const taskType = userRequest?.taskType;
  console.log('userRequest:', userRequest);

  console.log('GeneratedNotes - taskType value:', taskType, 'taskType type:', typeof taskType, 'userRequest?.taskType:', userRequest?.taskType);
  const [showWHView, _setShowWHView] = useState<boolean>(true);
  const [showKineticView, _setShowKineticView] = useState<boolean>(true);
  
  // Determine if specialized views can be shown using structured outputs
  let canShowWH = false;
  let canShowWHHistoryAndPhysical = false;
  let canShowKinetic = false;
  
  if (isBestiHealth && llmEnhancement) {
    // Check for History and Physical output format
    if (taskType === 'HISTORY_AND_PHYSICAL' && llmEnhancement.historyAndPhysicalOutput) {
      canShowWHHistoryAndPhysical = true;
    } 
    // Check for structured outputs with structuredSoap
    else if (llmEnhancement.wellWomanOutput?.structuredSoap || 
             llmEnhancement.prenatalOutput?.structuredSoap || 
             llmEnhancement.initialPrenatalOutput?.structuredSoap) {
      canShowWH = true;
    }
  }
  
  // KineticNotes doesn't have structured output yet, so it still needs rawOutput parsing
  if (isKineticNotes && llmEnhancement?.rawOutput) {
    // KineticNotes still uses rawOutput until structured outputs are implemented
    canShowKinetic = true;
  }

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    // Initialize all sections as expanded by default
    '0-SUBJECTIVE': true,
    '0-OBJECTIVE': true,
    '0-assessment': true,
    '0-PLAN': true,
    '0-transcript': true,
    
  });

  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  const toggleSection = (noteIndex: number, sectionName: string) => {
    const key = `${noteIndex}-${sectionName}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const copyToClipboard = async (text: string, buttonKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [buttonKey]: true }));
      // Reset the "copied" state after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [buttonKey]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleFeedback = (noteIndex: number, sectionName: string, isPositive: boolean) => {
    // TODO: Implement feedback functionality
    console.log(`Feedback for note ${noteIndex}, section ${sectionName}: ${isPositive ? 'positive' : 'negative'}`);
  };

  // Helper function to format headings
  const formatHeading = (text: string): string => {
    return text
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/([A-Z])([A-Z]+)/g, (_, first, rest) => {
        // Handle consecutive capitals (like REVIEW_OF_SYSTEMS -> Review of Systems)
        return first + rest.toLowerCase();
      })
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Helper function to format content with proper spacing and headings
  const formatContent = (content: string): string => {
    return content
      .replace(/\\n/g, '\n') // Convert escaped newlines
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove markdown bold formatting
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return ''; // Remove empty lines
        
        // Check if this looks like a heading (all caps with underscores or PascalCase)
        if (trimmed.match(/^[A-Z_]+$/) || trimmed.match(/^[A-Z][a-z]+([A-Z][a-z]*)*$/)) {
          return '\n' + formatHeading(trimmed) + '\n';
        }
        
        return trimmed;
      })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
      .replace(/^\n+|\n+$/g, '') // Remove leading/trailing newlines
      .trim();
  };

  // Helper function to get section content
  const getSectionContent = (note: HealthCareTranscribeResponse, sectionName: string): string => {
    const section = note.clinicalDocumentation?.ClinicalDocumentation?.sections?.find(
      s => s.sectionName === sectionName
    );
    
    if (!section?.summary) return '';
    
    let summary = '';
    
    // Handle different summary formats
    if (typeof section.summary === 'string') {
      summary = section.summary;
    } else if (Array.isArray(section.summary)) {
      // If it's an array, extract text from each object
      summary = section.summary
        .map((item: any) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            // Check for summarizedSegment first (this seems to be the actual content)
            if (item.summarizedSegment) {
              return item.summarizedSegment;
            }
            // Try other common text properties
            return item.text || item.content || item.summary || item.value || '';
          }
          return String(item);
        })
        .filter(text => text.trim().length > 0) // Remove empty strings
        .join('\n\n'); // Use double newlines for better paragraph separation
    } else if (section.summary && typeof section.summary === 'object') {
      // If it's an object, try to extract text
      const obj = section.summary as any;
      summary = obj.text || obj.content || obj.summary || obj.value || JSON.stringify(obj);
    } else {
      summary = String(section.summary);
    }
    
    return formatContent(summary);
  };

  // Helper function to render formatted content with proper heading styling
  const renderFormattedContent = (content: string) => {
    const lines = content.split('\n');
    
    return (
      <div className="space-y-3">
        {lines.map((line, index) => {
          const trimmed = line.trim();
          if (!trimmed) return null;
          
          // Check if this is a heading (starts with capital letter and is short, or contains common medical headings)
          const isHeading = trimmed.match(/^(Review of Systems|Past Medical History|Chief Complaint|History of Present Illness|Physical Examination|assessment and Plan|Plan|Subjective|Objective|assessment|Plan)$/i) ||
                           (trimmed.length < 50 && trimmed.match(/^[A-Z][a-z\s]+$/));
          
          if (isHeading) {
            return (
              <h4 key={index} className="text-lg font-semibold text-gray-900 mt-4 mb-2 first:mt-0">
                {trimmed}
              </h4>
            );
          }
          
          // Regular paragraph
          return (
            <p key={index} className="text-gray-800 leading-relaxed">
              {trimmed}
            </p>
          );
        })}
      </div>
    );
  };

  const renderSectionActions = (noteIndex: number, sectionName: string, content: string) => {
    const copyButtonKey = `${noteIndex}-${sectionName}-copy`;
    const isCopied = copiedStates[copyButtonKey];

    return (
      <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
        <button
          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(content, copyButtonKey);
          }}
          title="Copy to clipboard"
        >
          <Copy className="w-3 h-3 mr-1" />
          {isCopied ? 'Copied!' : 'Copy'}
        </button>
        <button
          className="inline-flex items-center p-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300"
          onClick={(e) => {
            e.stopPropagation();
            handleFeedback(noteIndex, sectionName, true);
          }}
          title="Good"
        >
          <ThumbsUp className="w-3 h-3" />
        </button>
        <button
          className="inline-flex items-center p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300"
          onClick={(e) => {
            e.stopPropagation();
            handleFeedback(noteIndex, sectionName, false);
          }}
          title="Needs improvement"
        >
          <ThumbsDown className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // Only show "No Clinical Notes Generated" if there are no generatedNotes AND no LLM analysis
  // CMS-1500 and other workflows may only have LLM analysis without generatedNotes
  if (!finalResult?.generatedNotes?.length && !llmEnhancement) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 bg-orange-100 rounded-full mb-4">
          <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Clinical Notes Generated</h3>
          <p className="text-gray-600 mb-4">
            No clinical notes were generated from the uploaded content. This might be due to:
          </p>
          <ul className="text-sm text-gray-600 text-left space-y-1 mb-6 bg-gray-50 rounded-lg p-4">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Poor audio quality or unclear recording
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              No medical content detected in the audio
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Transcription processing errors
            </li>
              </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Optional WH toggle for testing */}
      {/* {canShowWH && (
        <div className="flex items-center justify-end">
          <div className="join">
            <button
              className={`btn btn-xs join-item ${!showWHView ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => _setShowWHView(false)}
              title="Show Standard Notes"
            >
              Standard
            </button>
            <button
              className={`btn btn-xs join-item ${showWHView ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => _setShowWHView(true)}
              title="Show Women's Health View"
            >
              WH View
            </button>
          </div>
        </div>
      )} */}

      {/* Render Annual Well Woman Visit view when task type matches */}
      {taskType === 'Annual_Well_Woman_Visit' && llmEnhancement && userRequest?.llmAnalysisEnhancement ? (
        <AnnualWellWomanVisitNotesComponent 
          llmAnalysisEnhancement={userRequest.llmAnalysisEnhancement}
          userRequest={userRequest}
        />
      ) : taskType === 'Prenatal_Visit' && llmEnhancement && userRequest?.llmAnalysisEnhancement ? (
        <PrenatalVisitNotesComponent 
          llmAnalysisEnhancement={userRequest.llmAnalysisEnhancement}
          userRequest={userRequest}
        />
      ) : taskType === 'Initial_Prenatal_Visit' && llmEnhancement && userRequest?.llmAnalysisEnhancement ? (
        <PrenatalVisitInitialNotesComponent 
          llmAnalysisEnhancement={userRequest.llmAnalysisEnhancement}
          userRequest={userRequest}
        />
      ) : canShowWHHistoryAndPhysical ? (
        <WomensHealthHistoryAndPhysicalNotesComponent 
          llmAnalysisEnhancement={userRequest!.llmAnalysisEnhancement!}
          userRequest={userRequest!}
        />
      ) : canShowWH && showWHView ? (
        <WomensHealthGeneratedNotesComponent 
          llmAnalysisEnhancement={userRequest!.llmAnalysisEnhancement!}
          userRequest={userRequest!}
        />
      ) : canShowKinetic && showKineticView ? (
        <KineticNotesGeneratedNotesComponent 
          llmAnalysisEnhancement={userRequest!.llmAnalysisEnhancement!}
          userRequest={userRequest!}
        />
      ) : (
      finalResult?.generatedNotes?.map((note: HealthCareTranscribeResponse, noteIndex) => (
        <div key={noteIndex} className="card bg-base-100 rounded-lg border border-base-300 shadow-sm">
          {/* Note Header */}
          <div className="border-b border-base-300 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-base-content">Clinical Notes #{noteIndex + 1}</h3>
              <button 
                className="btn btn-sm btn-outline"
                onClick={() => copyToClipboard(note.transcript || '', `${noteIndex}-copyall`)}
                title="Copy entire note"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copiedStates[`${noteIndex}-copyall`] ? 'Copied!' : 'Copy All'}
              </button>
            </div>
          </div>

          {/* Note Content */}
          <div className="p-6 space-y-6">
            {/* SOAP Note Sections */}
            <div className="space-y-4">
              {['SUBJECTIVE', 'OBJECTIVE', 'assessment', 'PLAN'].map((sectionName) => {
                const content = getSectionContent(note, sectionName);
                const isExpanded = expandedSections[`${noteIndex}-${sectionName}`];
                
                if (!content) return null;
                
                return (
                  <div key={sectionName} className="border border-base-300 rounded-lg">
                      <div className="flex items-center justify-between p-4">
                        <button
                          className="flex-1 flex items-center space-x-3 text-left hover:bg-base-200 transition-colors duration-200 focus:outline-none rounded p-2 -m-2"
                          onClick={() => toggleSection(noteIndex, sectionName)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-base-content/50" />
                          ) : (
                            <ChevronUp className="w-5 h-5 text-base-content/50" />
                          )}
                          <span className="font-medium text-base-content">{sectionName}</span>
                        </button>
                        {renderSectionActions(noteIndex, sectionName, content)}
                      </div>
                    
                    {isExpanded && (
                      <div className="px-6 pb-6">
                        <div className="bg-base-200 rounded-lg p-8">
                          <div className="max-w-2xl">
                            {renderFormattedContent(content)}
                    </div>
                  </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* Transcript Section */}
            {note.transcript && (
              <div className="border border-base-300 rounded-lg">
                <div className="flex items-center justify-between p-4">
                  <button
                    className="flex-1 flex items-center space-x-3 text-left hover:bg-base-200 transition-colors duration-200 focus:outline-none rounded p-2 -m-2"
                    onClick={() => toggleSection(noteIndex, 'transcript')}
                  >
                    {expandedSections[`${noteIndex}-transcript`] ? (
                      <ChevronDown className="w-5 h-5 text-base-content/50" />
                    ) : (
                      <ChevronUp className="w-5 h-5 text-base-content/50" />
                    )}
                    <span className="font-medium text-base-content">Transcript</span>
                  </button>
                  {renderSectionActions(noteIndex, 'transcript', note.transcript)}
                </div>
                
                {expandedSections[`${noteIndex}-transcript`] && (
                  <div className="px-6 pb-6">
                    <div className="bg-base-200 rounded-lg p-8">
                      <div className="max-w-2xl">
                        {renderFormattedContent(note.transcript)}
                    </div>
                  </div>
                    </div>
                  )}
              </div>
            )}

            {/* Medical Comprehend Content (inline, no outer label wrapper) */}
            {note.cmAugmentation && (
              <div className="bg-base-200 rounded-lg p-8">
                <MedicalComprehendComponent 
                  cmAugmentation={note.cmAugmentation}
                  transcript={note.transcript}
                />
              </div>
            )}
          </div>
        </div>
      )))}
    </div>
  );
}; 